import React, { useState, useEffect } from "react";
import { useLocation, useRouteMatch } from "react-router-dom";
import { Container } from "reactstrap";
import { TabContent, Alert, Nav, Button, Row, Col } from "reactstrap";
import "./index.scss";
import SiteInformationTab from "../../components/SiteInformationTab";
import RentalInformationTab from "../../components/RentalInformationTab";
import ApiCaller from "../../../../api/apiCaller";
import Loading from "../../../../components/Loading";
import ImagesTab from "../../components/ImageTab";
import LegalDocumentTab from "../../components/LegalDocumentTab";
import PopulationInformationTab from "../../components/PopulationInfoTab";
import SiteFinancialProjectionV2 from "../../../../components/SiteFinancialProjectionV2";
import SummaryTab from "../../components/SummaryTab";
import SiteCommentTab from "../../components/SiteCommentTab";
import CustomAlert from "../../../../components/CustomAlert";
import SiteChangeLogTab from "../../components/SiteChangeLogTab";
import SiteActivityTab from "../../components/SiteActivityTab";
import { translateErrorMessage } from "utils/ErrorMessageTranslate";
import { connect } from "react-redux";
import OtherTab from "pages/Site/components/OtherTab";
import ExternalDashboard from "pages/Site/components/ExternalDashboard";
import ContractTab from "pages/Site/components/ContractTab";
import t from "utils/Lang";

interface StoreEditProps {
  siteDataProps: any;
  siteIdProps: any;
  userBrandId?: number;
}

const SiteEdit = ({
  siteDataProps,
  siteIdProps,
  userBrandId,
}: StoreEditProps) => {
  const isView = siteDataProps ? true : false;
  const pageUrl = useLocation();
  const isCreate = pageUrl?.pathname.includes("create") ? true : false;
  const match = useRouteMatch("/sitev2/:site_id/edit");
  const params: any = match?.params;
  const siteId = isView ? siteIdProps : params?.site_id;
  // Find search params in URL
  const search = window.location.search;
  const searchParams = new URLSearchParams(search);
  const defaultStoreFormatId = searchParams.get("store_format_id") || null;
  const defaultStoreTypeId = searchParams.get("store_type_id");
  const defaultLocationId = searchParams.get("location_id");
  const defaultActiveTab = searchParams.get("active_tab") || "summary_tab";
  let allowEditLocationAddress = defaultLocationId ? true : false;

  const defaultSiteData: any = {
    store_format: {
      id: defaultStoreFormatId,
    },
    store_type: {
      id: defaultStoreTypeId,
    },
    location: {
      id: defaultLocationId,
    },
  };

  // Options
  const [statusOptions, setStatusOptions] = useState([]);
  const [contractRentalTypeOptions, setContractRentalTypeOptions] = useState(
    []
  );
  const [siteTypeOptions, setSiteTypeOptions] = useState([]);
  const [customerGroupOptions, setCustomerGroupOptions] = useState([]);
  const [storeFormatOptions, setStoreFormatOptions] = useState([]);
  const [storeTypeOptions, setStoreTypeOptions] = useState([]);
  const [parkingSiteTypeOptions, setParkingSiteTypeOptions] = useState([]);
  const [
    externalEnvironmentFactorOptions,
    setExternalEnvironmentFactorOptions,
  ] = useState([]);
  const [siteSurroundingAreaTypeOptions, setSiteSurroundingAreaTypeOptions] =
    useState([]);
  const [sitePositionTypeOptions, setSitePositionTypeOptions] = useState([]);
  const [locationTypeOptions, setLocationTypeOptions] = useState([]);
  const [invalidFields, setInvalidFields] = useState<any>({});
  const [invalidFieldsDisplay, setInvalidFieldsDisplay] = useState<Array<any>>(
    []
  );

  const [existingAttachmentsToUpdate, setExistingAttachmentsToUpdate] =
    useState<Array<any>>([]);

  const [activeTab, setActiveTab] = useState(
    isView ? defaultActiveTab : "store_information_tab"
  );
  const [isInputValid, setIsInputValid] = useState(true);

  const [siteData, setSiteData] = useState({
    data: isView ? siteDataProps?.data : null,
    labels: isView ? siteDataProps?.labels : null,
    loading: !isView,
    customFields: isView ? siteDataProps?.customFields : null,
    contracts: isView ? siteDataProps?.contracts : null,
  });
  const [selectedPhotos, setSelectedPhotos] = useState<Array<any>>([]);

  const [alertData, setAlertData] = useState({
    isShow: false,
    content: "",
    color: "danger",
  });

  const toggle = (tab: string) => () => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const getStoreDetail = async () => {
    const [
      siteLabelsRes,
      res,
      statusOptionsRes,
      customerGroupOptionRes,
      storeFormatOptionRes,
      storeTypeOptionRes,
      parkingSiteTypeOptionRes,
      sitePositionTypeOptionRes,
      externalEnvironmentFactorOptionsRes,
      siteSurroundingAreaTypeRes,
      locationTypeRes,
      contractRentalTypeRes,
      siteTypeRes,
      customFieldRes,
      contractRes,
    ] = await Promise.all([
      ApiCaller.get("site/_labels"),
      !isCreate ? ApiCaller.get(`site/${siteId}`) : null,
      ApiCaller.get(
        `status/?q=(filters:!((col:content_type,opr:eq,value:store)))`
      ),
      ApiCaller.get("customer_group/"),
      ApiCaller.get("store_format/"),
      ApiCaller.get("store_type/"),
      ApiCaller.get("parking_site_type/"),
      ApiCaller.get("site_position_type/"),
      ApiCaller.get("external_environmental_factor/"),
      ApiCaller.get("site_surrounding_area_type/"),
      ApiCaller.get("location_type/"),
      ApiCaller.get("contract_rental_type/"),
      ApiCaller.get("site_type/"),
      ApiCaller.get("studio/site"),
      !isCreate ? ApiCaller.get(`site/${siteId}`) : null,
    ]);
    if (siteLabelsRes) {
      Object.keys(siteLabelsRes).forEach(function (key) {
        if (!defaultSiteData[key]) {
          defaultSiteData[key] = null;
        }
      });

      if (defaultLocationId) {
        const locationRes = await ApiCaller.get(
          `location/${defaultLocationId}`
        );
        if (locationRes?.result) {
          const locationData = locationRes?.result;
          defaultSiteData.location = locationRes?.result;
          defaultSiteData.city = locationData.city;
          defaultSiteData.district = locationData.district;
          defaultSiteData.ward = locationData.ward;
          defaultSiteData.location_name = locationData.name;
          defaultSiteData.latitude = locationData.latitude;
          defaultSiteData.longitude = locationData.longitude;
        }

        const userRes = await ApiCaller.get("userme/me");
        if (userRes?.result) {
          const defaultBrandLocationType =
            userRes.result.user_brand.default_brand_location_type;
          defaultSiteData.location_type = defaultBrandLocationType;
        }
      }
      // Add label data to site object
      setSiteData({
        data: defaultSiteData,
        labels: siteLabelsRes,
        loading: false,
        customFields: customFieldRes.template,
        contracts: contractRes?.result?.contracts || [],
      });
    }
    if (statusOptionsRes?.result) {
      setStatusOptions(
        statusOptionsRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (customerGroupOptionRes?.result) {
      setCustomerGroupOptions(
        customerGroupOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (storeFormatOptionRes?.result) {
      setStoreFormatOptions(
        storeFormatOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (storeTypeOptionRes?.result) {
      setStoreTypeOptions(
        storeTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (parkingSiteTypeOptionRes?.result) {
      setParkingSiteTypeOptions(
        parkingSiteTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (sitePositionTypeOptionRes?.result) {
      setSitePositionTypeOptions(
        sitePositionTypeOptionRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (externalEnvironmentFactorOptionsRes?.result) {
      setExternalEnvironmentFactorOptions(
        externalEnvironmentFactorOptionsRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (siteSurroundingAreaTypeRes?.result) {
      setSiteSurroundingAreaTypeOptions(
        siteSurroundingAreaTypeRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (locationTypeRes?.result) {
      setLocationTypeOptions(
        locationTypeRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (contractRentalTypeRes?.result) {
      setContractRentalTypeOptions(
        contractRentalTypeRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    if (siteTypeRes?.result) {
      setSiteTypeOptions(
        siteTypeRes?.result?.map((item: any) => {
          return {
            value: item?.id,
            label: item?.name,
          };
        })
      );
    }
    // Process site data
    if (res?.result) {
      setSiteData({
        data: res?.result,
        labels: res?._labels,
        loading: false,
        customFields: customFieldRes.template,
        contracts: contractRes?.result?.contracts || [],
      });
    }
  };

  const lookUpFieldLabel = (fieldName: string) => {
    if (fieldName) {
      return siteData.labels[fieldName];
    } else {
      return "";
    }
  };

  const validateInputData = () => {
    const requiredFields = ["name"];
    const siteDataObject = siteData.data;
    let newInvalidFields: { [key: string]: any } = {};
    let newInvalidFieldsDisplay: Array<any> = [];
    let isValid = true;
    requiredFields.forEach((requiredField) => {
      if (!siteDataObject[requiredField]) {
        newInvalidFields[requiredField] = "required";
        const fieldLabel = lookUpFieldLabel(requiredField);
        newInvalidFieldsDisplay.push({
          field: requiredField,
          type: "required",
          text: `${fieldLabel} không được để trống`,
          label: fieldLabel,
          message: `không được để trống`,
        });
        isValid = false;
      }
    });
    if (!isValid) {
      setInvalidFields(newInvalidFields);
      setIsInputValid(false);
      setInvalidFieldsDisplay(newInvalidFieldsDisplay);
      return false;
    } else {
      setInvalidFields({});
      setIsInputValid(true);
      setInvalidFieldsDisplay([]);
      return true;
    }
  };

  const updateSiteData = async (goToViewPage = false) => {
    const canContinue = validateInputData();
    if (!canContinue) return;

    setSiteData({ ...siteData, ...{ loading: true } });

    let res: any = null;
    if (!isCreate) {
      res = await ApiCaller.put(`site/${siteId}`, siteData.data);
    } else {
      res = await ApiCaller.post("site/", siteData.data);
    }

    if (res?.result) {
      // Upload new attachments
      if (selectedPhotos.length > 0) {
        let promiseNewAttachmentAll: Array<any> = [];
        selectedPhotos.forEach(async (attachment: any) => {
          var attachmentFormData = new FormData();
          attachmentFormData.append("user_file", attachment.blob);
          attachmentFormData.append(
            "caption_text",
            attachment.captionText || null
          );
          attachmentFormData.append(
            "legal_document_id",
            attachment.legalDocumentId || null
          );
          promiseNewAttachmentAll.push(
            ApiCaller.post(
              `site_attachment/${siteId || res?.id}`,
              attachmentFormData
            )
          );
        });
        setSiteData({ ...siteData, ...{ loading: true } });
        const resNewAttachmentData = await Promise.all(promiseNewAttachmentAll);
        setSiteData({ ...siteData, ...{ loading: false } });
        console.log(resNewAttachmentData);
      }

      // Update existing attachment
      if (existingAttachmentsToUpdate) {
        let promiseExistingAttachmentAll: Array<any> = [];
        existingAttachmentsToUpdate.forEach(async (attachment: any) => {
          var attachmentFormData = new FormData();
          attachmentFormData.append("id", attachment.id);
          attachmentFormData.append(
            "caption_text",
            attachment.caption_text || attachment.captionText || null
          );
          attachmentFormData.append(
            "legal_document_id",
            attachment.legal_document_id || attachment.legalDocumentId || null
          );
          promiseExistingAttachmentAll.push(
            ApiCaller.put(`site_attachment/${siteId}`, attachmentFormData)
          );
        });
        setSiteData({ ...siteData, ...{ loading: true } });
        const resAttachmentData = await Promise.all(
          promiseExistingAttachmentAll
        );
        setSiteData({ ...siteData, ...{ loading: false } });
        console.log(resAttachmentData);
      }

      // Clear all photo set
      setSelectedPhotos([]);

      if (isCreate) {
        window.location.href = `/sitev2/${res.id}/edit`;
      }

      if (goToViewPage) {
        window.location.href = `/sitev2/${res.id || siteId}/`;
      } else {
        getStoreDetail();
      }
    } else {
      try {
        let errorMessage = "";
        if (typeof res?.message === "object" && res?.message !== null) {
          const keys = Object.keys(res?.message || []);
          keys.map((k, index) => {
            const label = siteData?.labels?.[k] || k;
            const msg = translateErrorMessage(
              res?.message?.[k]?.toString?.() || "Chưa xác định"
            );

            if (index == 0) {
              errorMessage += `• ${label}: ${msg}`;
            } else {
              errorMessage += `\n• ${label}: ${msg}`;
            }
          });
        } else {
          errorMessage = res?.message;
        }
        setAlertData({
          ...alertData,
          ...{ content: errorMessage, isShow: true },
        });
      } catch (e) {
        console.error("Unknown error when saving site...", e);
      }
    }
    setSiteData({ ...siteData, ...{ loading: false } });
  };

  const onImageRemoved = (imageAttribute: string) => {
    if (imageAttribute) {
      delete siteData?.data?.photos[imageAttribute];
      setSiteData({ ...siteData });
    }
  };

  const onExistingAttachmentsUpdated = (existingAttachments: any) => {
    setExistingAttachmentsToUpdate(existingAttachments);
  };

  useEffect(() => {
    if (!isView) {
      getStoreDetail();
    }
  }, []);

  if (siteData.loading) return <Loading />;

  // When editing an in-building site

  // On Create new site and default store type is not in-building
  // Allow edit location
  if (isCreate && defaultStoreFormatId == "1") {
    allowEditLocationAddress = true;
  }
  if (!isView && !defaultStoreFormatId) {
    allowEditLocationAddress = true;
  }

  const legalPermissions = siteData?.data?.legal_permissions || [];

  let tabData =
    siteData.customFields?.map?.((tab: any) => ({
      label: tab.name,
      id: tab.scope === "custom" ? `${tab.id}` : tab.code,
      badge:
        tab.code === "site_comment_tab"
          ? siteData.data.site_comment_count
          : undefined,
    })) || [];
  const customTab =
    siteData.customFields?.filter?.((tab: any) => tab.scope === "custom") || [];
  if (!isView) {
    tabData = tabData.filter((t: any) => t.id !== "summary_tab");
  }
  if (!legalPermissions.includes("can_get")) {
    tabData = tabData.filter((t: any) => t.id !== "site_legal_document_tab");
  }
  if (isCreate) {
    tabData = tabData.filter(
      (t: any) =>
        t.id !== "site_comment_tab" &&
        t.id !== "site_activity_tab" &&
        t.id !== "site_change_log_tab"
    );
  }
  const body = (
    <>
      {!isView && (
        <CustomAlert
          {...{
            content: alertData.content,
            isShow: alertData.isShow,
            color: alertData.color,
          }}
        />
      )}
      {(!isView || (isView && activeTab === "site_legal_document_tab")) && (
        <div style={styles.siteEditControls}>
          <div style={styles.siteEditControlButtons}>
            {siteId && (
              <Button
                outline
                color="primary"
                onClick={() => updateSiteData(false)}
                style={{ marginRight: 12 }}
              >
                {t().action__save_and_continue}
              </Button>
            )}
            <Button color="primary" onClick={() => updateSiteData(true)}>
              {t().action__save_and_preview}
            </Button>
          </div>
        </div>
      )}
      {!isInputValid && (
        <Row>
          <Col xs={12}>
            <Alert color="danger">
              {invalidFieldsDisplay.map((invalidField: any, index) => (
                <p key={index}>
                  <b>{invalidField.text}</b> {invalidField.message}
                </p>
              ))}
            </Alert>
          </Col>
        </Row>
      )}
      <Nav tabs className="site-edit-tab">
        {tabData.map((data: any) => {
          return (
            <div
              key={data.id}
              className={`normal-button site-edit-tab-item ${
                activeTab === data.id ? "active" : ""
              }`}
              onClick={toggle(data.id)}
            >
              <span>{data.label}</span>
              {!!data.badge && (
                <span className="tab-item-badge">{data.badge}</span>
              )}
            </div>
          );
        })}
      </Nav>
      <TabContent activeTab={activeTab}>
        <ContractTab
          {...{
            isView,
            siteData,
            setSiteData,
            siteId,
          }}
        />
        {customTab.map((t: any) => (
          <OtherTab
            {...{
              isView,
              siteData,
              setSiteData,
            }}
            groups={t.groups}
            key={t.id}
            tabId={`${t.id}`}
          />
        ))}
        <SummaryTab
          {...{
            isView,
            siteData,
            siteId,
          }}
        />
        <SiteInformationTab
          {...{
            isView,
            allowEditLocationAddress,
            siteData,
            setSiteData,
            statusOptions,
            customerGroupOptions,
            storeFormatOptions,
            storeTypeOptions,
            parkingSiteTypeOptions,
            externalEnvironmentFactorOptions,
            siteSurroundingAreaTypeOptions,
            sitePositionTypeOptions,
            locationTypeOptions,
            invalidFields,
            siteTypeOptions,
          }}
          groups={
            siteData?.customFields?.find?.(
              (t: any) => t.code === "store_information_tab"
            )?.groups || []
          }
        />
        <ImagesTab
          {...{
            isView,
            siteData,
            setSiteData,
            selectedPhotos,
            setSelectedPhotos,
            onImageRemoved,
            onExistingAttachmentsUpdated,
          }}
        />
        {activeTab === "site_legal_document_tab" && (
          <LegalDocumentTab
            {...{
              isView: false,
              siteData,
              setSiteData,
              selectedPhotos,
              setSelectedPhotos,
              onImageRemoved,
              onExistingAttachmentsUpdated,
            }}
          />
        )}
        <RentalInformationTab
          {...{
            isView,
            isCreate,
            defaultStoreFormatId,
            siteData,
            setSiteData,
            statusOptions,
            customerGroupOptions,
            storeFormatOptions,
            storeTypeOptions,
            parkingSiteTypeOptions,
            externalEnvironmentFactorOptions,
            siteSurroundingAreaTypeOptions,
            sitePositionTypeOptions,
            siteId,
            contractRentalTypeOptions,
          }}
          groups={
            siteData?.customFields?.find?.(
              (t: any) => t.code === "rental_info_tab"
            )?.groups || []
          }
        />
        <PopulationInformationTab
          {...{
            isView,
            siteData,
            setSiteData,
          }}
        />
        {!isCreate && (
          <SiteFinancialProjectionV2
            isView={isView}
            site={siteData?.data || { id: siteId }}
            siteId={siteId}
          />
        )}
        {!isCreate && activeTab === "site_comment_tab" && (
          <SiteCommentTab
            isView={isView}
            site={siteData?.data || { id: siteId }}
            siteId={siteId}
            isCanEdit={siteData?.data?.status?.is_site_comment_can_edit}
            userBrandId={userBrandId}
          />
        )}
        {activeTab === "site_change_log_tab" && (
          <SiteChangeLogTab isView={isView} siteId={siteId} />
        )}
        {activeTab === "site_activity_tab" && (
          <SiteActivityTab isView={isView} siteId={siteId} />
        )}
        {activeTab === "external_dashboard_tab" && (
          <ExternalDashboard site={siteData.data} />
        )}
      </TabContent>
    </>
  );
  if (isView) {
    return <div style={{ marginTop: 20 }}>{body}</div>;
  }
  return <Container>{body}</Container>;
};

const styles = {
  siteEditControls: {
    marginTop: 24,
    marginBottom: 24,
  },
  siteEditControlButtons: {
    display: "flex",
    justifyContent: "flex-end",
  },
};

const mapStateToProps = (state: any) => {
  return {
    userBrandId: state.user?.data?.user_brand?.brand_id,
  };
};

export default connect(mapStateToProps)(SiteEdit);
